import React from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';
import {
  StyledPaperContainer,
  StyledSectionSubtitle,
  StyledSectionTitle
} from '../../../PlusWebsiteEditor/PlusWebsiteEditor.styles';
import { ColumnWithGap } from '../../../PlusWebsiteEditor/sections/HeroBlock/HeroBlock.styled';
import { ClinicWithConditionsAndPromo } from '../../types';
import { SeoLockedFaqsNotice, StyledSeoNote } from '../../SeoLockedContent';
import TextEditor from '../../../FormEditors/TextEditor';
import HtmlEditor from '../../../FormEditors/HtmlEditor';
import ImageEditor from '../../../FormEditors/ImageEditor';
import RepeatableRowsEditor from '../../../FormEditors/RepeatableRowsEditor';
import ClinicalReviewEditor from '../../ClinicalReviewEditor';

type PromotableFormProps = Readonly<{
  form: UseFormReturn<ClinicWithConditionsAndPromo>;
}>;

type ClinicPromoEditorProps = PromotableFormProps & Readonly<{ isCreateMode?: boolean }>;

// Dynamic per-row field paths can't be statically inferred; cast to the form's FieldPath.
type ClinicFieldName = FieldPath<ClinicWithConditionsAndPromo>;

// The reviewer is one of the clinic's prescribers, which a clinic only has once it exists.
function ClinicPromoEditor({ form, isCreateMode = false }: ClinicPromoEditorProps) {
  return (
    <>
      <StyledSectionTitle>Promotional Content</StyledSectionTitle>
      <PromoScreenEditor form={form} />
      <HeroBenefitsEditor form={form} />
      <HowItWorksEditor form={form} />
      <FaqsEditor form={form} />
      {!isCreateMode && (
        <StyledPaperContainer>
          <ClinicalReviewEditor form={form} name="promotional_content" pageLabel="Approved by" page="the clinic page" />
        </StyledPaperContainer>
      )}
      <HomeScreenEditor form={form} />
      <ShopCardEditor form={form} />
    </>
  );
}

function FaqsEditor({ form }: PromotableFormProps) {
  return (
    <StyledPaperContainer>
      <ColumnWithGap>
        <StyledSectionTitle>FAQs (Clinic page)</StyledSectionTitle>
        <SeoLockedFaqsNotice page="clinic" />
        <RepeatableRowsEditor
          control={form.control}
          name="promotional_content.faqs"
          label="Questions"
          addButtonLabel="Add FAQ"
          createEmptyRow={() => ({ question: '', answer: '' })}
          renderRow={(rowName) => (
            <>
              <TextEditor label="Question" control={form.control} name={`${rowName}.question` as ClinicFieldName} />
              <HtmlEditor label="Answer" control={form.control} name={`${rowName}.answer` as ClinicFieldName} />
            </>
          )}
        />
      </ColumnWithGap>
    </StyledPaperContainer>
  );
}

function HeroBenefitsEditor({ form }: PromotableFormProps) {
  return (
    <StyledPaperContainer>
      <ColumnWithGap>
        <StyledSectionTitle>Hero benefits</StyledSectionTitle>
        <StyledSectionSubtitle>
          Trust bullets shown in the hero and the green benefit bar. Leave empty to use the built-in default
          benefits.
        </StyledSectionSubtitle>
        <StyledSeoNote>
          🔒 On partner pharmacy websites the THIRD benefit is replaced by a generated collection/delivery
          benefit (appended when fewer than three are authored); other benefits render as authored. The
          Healthera site shows the full list as authored.
        </StyledSeoNote>
        <RepeatableRowsEditor
          control={form.control}
          name="promotional_content.hero_benefits"
          label="Benefits"
          addButtonLabel="Add benefit"
          createEmptyRow={() => ({ icon_url: '', text: '' })}
          renderRow={(rowName) => (
            <>
              <ImageEditor
                control={form.control}
                name={`${rowName}.icon_url` as ClinicFieldName}
                label="Icon"
                suggestedSize="60x60px"
                height="60px"
                width="60px"
              />
              <TextEditor label="Text" control={form.control} name={`${rowName}.text` as ClinicFieldName} />
            </>
          )}
        />
      </ColumnWithGap>
    </StyledPaperContainer>
  );
}

function HowItWorksEditor({ form }: PromotableFormProps) {
  return (
    <StyledPaperContainer>
      <ColumnWithGap>
        <StyledSectionTitle>How it works</StyledSectionTitle>
        <StyledSeoNote>
          🔒 On partner pharmacy websites the LAST step&apos;s description is replaced by a generated
          collection/delivery sentence; earlier steps render as authored.
        </StyledSeoNote>
        <TextEditor label="Section title" control={form.control} name="promotional_content.how_it_works.title" />
        <TextEditor label="Section subtitle" control={form.control} name="promotional_content.how_it_works.subtitle" />
        <RepeatableRowsEditor
          control={form.control}
          name="promotional_content.how_it_works.steps"
          label="Steps"
          addButtonLabel="Add step"
          createEmptyRow={() => ({ title: '', description: '' })}
          renderRow={(rowName) => (
            <>
              <TextEditor label="Step title" control={form.control} name={`${rowName}.title` as ClinicFieldName} />
              <TextEditor label="Step description" control={form.control} name={`${rowName}.description` as ClinicFieldName} />
            </>
          )}
        />
      </ColumnWithGap>
    </StyledPaperContainer>
  );
}

function PromoScreenEditor({ form }: PromotableFormProps) {
  return (
    <StyledPaperContainer>
      <ColumnWithGap>
        <StyledSectionSubtitle>Promo screen</StyledSectionSubtitle>
        <PromoTitleEditor form={form} />
        <StyledSeoNote>
          🔒 Partner pharmacy websites replace this title with the generated SEO H1 (&quot;Private Clinic in
          [town]&quot;) and insert a generated subheading below it. This title is used on the Healthera site.
        </StyledSeoNote>
        <PromoDescriptionEditor form={form} />
        <PromoDisclaimerEditor form={form} />
        <PromoImageEditor form={form} />
      </ColumnWithGap>
      <ColumnWithGap>
        <PromoZingerEditor form={form} />
      </ColumnWithGap>
    </StyledPaperContainer>
  );
}

function PromoTitleEditor({ form }: PromotableFormProps) {
  return <TextEditor label="Promo title" control={form.control} name="promotional_content.title" />;
}

function PromoDescriptionEditor({ form }: PromotableFormProps) {
  return <HtmlEditor label="Description" control={form.control} name="promotional_content.description" />;
}

function PromoDisclaimerEditor({ form }: PromotableFormProps) {
  return <HtmlEditor label="Disclaimer" control={form.control} name="promotional_content.disclaimer" />;
}

function PromoImageEditor({ form }: PromotableFormProps) {
  return (
    <ImageEditor
      control={form.control}
      name="promotional_content.image_url"
      suggestedSize="1125x630"
      height="210px"
      width="375px"
    />
  );
}

function PromoZingerEditor({ form }: PromotableFormProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>Zinger</StyledSectionTitle>
      <PromoZingerTextEditor form={form} />
      <PromoZingerIconEditor form={form} />
    </ColumnWithGap>
  );
}

function PromoZingerTextEditor({ form }: PromotableFormProps) {
  return <TextEditor label="Zinger text" control={form.control} name="promotional_content.zinger.description" />;
}

function PromoZingerIconEditor({ form }: PromotableFormProps) {
  return (
    <ImageEditor
      control={form.control}
      name="promotional_content.zinger.icon_url"
      suggestedSize="60x60"
      height="140px"
      width="140px"
    />
  );
}

function HomeScreenEditor({ form }: PromotableFormProps) {
  return (
    <StyledPaperContainer>
      <ColumnWithGap>
        <StyledSectionTitle>Home card promotion</StyledSectionTitle>
        <HomePromoTitleEditor form={form} />
        <HomePromoDescriptionEditor form={form} />
        <HomeBannerEditor form={form} />
      </ColumnWithGap>
    </StyledPaperContainer>
  );
}

function HomePromoTitleEditor({ form }: PromotableFormProps) {
  return <TextEditor label="Home card title" control={form.control} name="promotional_content.home_card.title" />;
}

function HomePromoDescriptionEditor({ form }: PromotableFormProps) {
  return (
    <HtmlEditor label="Home card description" control={form.control} name="promotional_content.home_card.description" />
  );
}

function HomeBannerEditor({ form }: PromotableFormProps) {
  return (
    <ImageEditor
      control={form.control}
      name="promotional_content.home_card.image_url"
      suggestedSize="960x540"
      height="180px"
      width="320px"
    />
  );
}

function ShopCardEditor({ form }: PromotableFormProps) {
  return (
    <StyledPaperContainer>
      <ColumnWithGap>
        <StyledSectionTitle>Shop card promotion</StyledSectionTitle>
        <ShopCardTitleEditor form={form} />
        <ShopCardTaglineEditor form={form} />
        <ShopCardDescription form={form} />
        <ShopCardImage form={form} />
      </ColumnWithGap>
    </StyledPaperContainer>
  );
}

function ShopCardTitleEditor({ form }: PromotableFormProps) {
  return <TextEditor label="Shop card title" control={form.control} name="promotional_content.shop_card.title" />;
}

function ShopCardTaglineEditor({ form }: PromotableFormProps) {
  return <TextEditor label="Shop card tagline" control={form.control} name="promotional_content.shop_card.tagline" optional />;
}

function ShopCardImage({ form }: PromotableFormProps) {
  return (
    <ImageEditor
      control={form.control}
      name="promotional_content.shop_card.image_url"
      suggestedSize="444x249"
      height="125px"
      width="222px"
    />
  );
}

function ShopCardDescription({ form }: PromotableFormProps) {
  return (
    <HtmlEditor label="Shop card description" control={form.control} name="promotional_content.shop_card.description" />
  );
}

export default ClinicPromoEditor;
